import { getClientById } from 'api/v2/clients';
import { calculateItems, getRevenueById } from 'api/v2/revenues';
import { Loader } from 'components/atoms/Loader';
import SimpleError from 'components/molecules/SimpleError.component';
import dayjs from 'dayjs';
import useQueryParams from 'hooks/shared/useQueryParams';
import useCustomNavigate from 'hooks/useCustomeNavigate';
import useCutRouterState from 'hooks/useCutRouterState';
import { lazy, useEffect, useId, useMemo, type FC } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router';
import { RevenuesCacheKeys } from 'types/cacheKeys.types';
import { CurrenciesEnum } from 'types/global.types';
import { Invoice, InvoiceTypeEnum } from 'types/invoice.types';
import {
  isDocumentLockedByTaxesOrAccountant,
  isNonEditableInstallmentInvoice,
} from 'utils/helpers';
import { cleanInvoiceItems } from '../SharedControls/ItemsForm/helpers';
import '../styles.scss';
const Form = lazy(() => import('./Form'));

const PrepareInvoice: FC<{ data: Partial<Invoice> }> = ({ data }) => {
  const invoice = useMemo(
    () => ({
      ...data,
      items: cleanInvoiceItems(data.items || []),
      ...(data.revenueProject && {
        revenueProject: {
          ...data.revenueProject,
          snapshot: {
            ...data.revenueProject.snapshot,
            items: cleanInvoiceItems(data.revenueProject.snapshot?.items || []),
          },
        },
      }),
    }),
    [data],
  );

  const clientQuery = useQuery(
    [RevenuesCacheKeys.ClientById, useId()],
    () => {
      const clientId = invoice.client?._id;
      if (!clientId) {
        return {
          client: undefined,
          clientId: undefined,
        };
      }
      return getClientById(clientId, { ignoreToast: true })
        .then(() => ({
          client: invoice!.client,
          clientId: clientId,
        }))
        .catch((e) => {
          if (e.response?.status === 404) {
            return {
              client: undefined,
              clientId: undefined,
            };
          }

          throw e;
        });
    },
    {
      cacheTime: 0,
    },
  );

  const revenueItemsCalculationsQueryEnabled =
    !!clientQuery.data && !!invoice.items.length;
  const revenueItemsCalculationsQuery = useQuery(
    [
      RevenuesCacheKeys.ItemsCalculations,
      useId(),
      revenueItemsCalculationsQueryEnabled,
    ],
    () => {
      const client = clientQuery.data!;
      return calculateItems({
        items: invoice.items,
        type: invoice.type || InvoiceTypeEnum.INVOICE,
        clientId: client.clientId,
        invoiceDate: invoice.invoiceDate || dayjs().format('YYYY-MM-DD'),
        options: {
          currency: invoice.currency || CurrenciesEnum.EUR,
        },
      });
    },
    { cacheTime: 0, enabled: revenueItemsCalculationsQueryEnabled },
  );

  const projectItemsCalculationsQueryEnabled =
    !!clientQuery.data && !!invoice.revenueProject?.snapshot?.items.length;
  const projectItemsCalculationsQuery = useQuery(
    [
      RevenuesCacheKeys.ProjectItemsCalculations,
      useId(),
      projectItemsCalculationsQueryEnabled,
    ],
    () => {
      const client = clientQuery.data!;
      return calculateItems({
        items: invoice.revenueProject?.snapshot?.items || [],
        type: invoice.type || InvoiceTypeEnum.INVOICE,
        clientId: client.clientId,
        invoiceDate: invoice.invoiceDate || dayjs().format('YYYY-MM-DD'),
        options: {
          currency: invoice.currency || CurrenciesEnum.EUR,
        },
      });
    },
    { cacheTime: 0, enabled: projectItemsCalculationsQueryEnabled },
  );

  const document = useMemo(() => {
    return {
      ...invoice,
      ...clientQuery.data,
      itemsCalculations: revenueItemsCalculationsQuery.data,
      projectItemsCalculations: projectItemsCalculationsQuery.data,
    };
  }, [
    invoice,
    clientQuery.data,
    revenueItemsCalculationsQuery.data,
    projectItemsCalculationsQuery.data,
  ]);

  const isLoading =
    clientQuery.isLoading ||
    revenueItemsCalculationsQuery.isLoading ||
    projectItemsCalculationsQuery.isLoading;
  const isError =
    clientQuery.isError ||
    revenueItemsCalculationsQuery.isError ||
    projectItemsCalculationsQuery.isError;

  if (isLoading) return <Loader />;

  if (isError)
    return (
      <SimpleError
        onRetry={() => {
          if (clientQuery.isError) clientQuery.refetch();
          if (revenueItemsCalculationsQuery.isError)
            revenueItemsCalculationsQuery.refetch();
        }}
      />
    );

  return <Form defaultValues={document as any} />;
};

export const EditInvoice = () => {
  const navigate = useCustomNavigate();
  const revenueId = useParams().revenueId as string;

  const revenueQuery = useQuery(
    [RevenuesCacheKeys.RevenueById, revenueId],
    () => getRevenueById(revenueId), // TODO: reordering items (if needed) has to be handled here
    { cacheTime: 0 },
  );

  const isLocked =
    !!revenueQuery.data &&
    isDocumentLockedByTaxesOrAccountant(revenueQuery.data);

  const isNonEditableInstallment =
    !!revenueQuery.data && isNonEditableInstallmentInvoice(revenueQuery.data);

  useEffect(() => {
    if (isLocked || isNonEditableInstallment) {
      navigate('/revenues?id=' + revenueId, { replace: true });
    }
  }, [isLocked, isNonEditableInstallment, revenueId]);

  if (revenueQuery.isLoading || isLocked) return <Loader />;

  if (revenueQuery.isError) {
    return (
      <SimpleError
        onRetry={() => {
          revenueQuery.refetch();
        }}
      />
    );
  }

  if (!revenueQuery.data) return null;
  return <PrepareInvoice data={revenueQuery.data} />;
};

export const CreateInvoice: FC = () => {
  const isCreditNote = useQueryParams().type === InvoiceTypeEnum.CREDIT_NOTE;

  const guessedDocument = useCutRouterState(
    'guessedDocument',
  ) as Partial<Invoice> | null;

  if (!guessedDocument) {
    return (
      <Form
        defaultValues={{
          type: isCreditNote
            ? InvoiceTypeEnum.CREDIT_NOTE
            : InvoiceTypeEnum.INVOICE,
        }}
      />
    );
  }

  return <PrepareInvoice data={{ ...guessedDocument, _id: undefined }} />;
};
